import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

import { Link } from '../link';
import { Wrapper } from '../../utils/components';
import { getLinkInformation } from '../../utils/functions';
import MegaMenuPanel from './MegaMenuPanel';
import { getColorsCategory } from '../../utils/functions';
import Icon from '../../atoms/icon';
import withPreview from '../../utils/with-preview';
import { HeaderContext } from '../../context/headerContext';
import useWithMobileResizeListener from '../../utils/hooks/useWithMobileResizeListener';

import './MainMenu.scss';

const MenuBusinessSecondLevelItem = ({ item }) => {
  const linkInformation = getLinkInformation(
    item.content.secondLevelInternalLink?.value?.link,
    item.content.secondLevelExternalLink.value,
    item.value,
    item.content.secondLevelInternalLink?.value?.pageTitle?.value
  );

  return (
    <Wrapper
      className='main-nav-secondary__item main-nav-secondary__item--desktop'
      to={linkInformation.link}
      tag='span'
      component={linkInformation.link && Link}
    >
      {linkInformation.label && <span>{linkInformation.label}</span>}
    </Wrapper>
  );
};

const MenuBusinessThirdLevelItem = ({ item }) => {
  const linkInformation = getLinkInformation(
    item.content.thirdLevelInternalLink?.value?.link,
    item.content.thirdLevelExternalLink.value,
    item.value,
    item.content.thirdLevelInternalLink?.value?.pageTitle?.value
  );

  return (
    <>
      {!item.content.thirdLevelLinkAsButton.value && (
        <li className='main-nav-tertiary__third-level-menu__item'>
          <Wrapper
            className='main-nav-tertiary__third-level-link'
            to={linkInformation.link}
            tag='span'
            component={linkInformation.link && Link}
          >
            {linkInformation.label && <span>{linkInformation.label}</span>}
          </Wrapper>
        </li>
      )}
      {item.content.thirdLevelLinkAsButton.value && (
        <li className='main-nav-tertiary__third-level-menu__item'>
          <Wrapper
            className='main-nav-tertiary__third-level-button'
            style={{
              '--bg-button-category': `var(${
                getColorsCategory(
                  item.content.thirdLevelInternalLink?.value?.liferayFields
                    ?.taxonomyCategoryBriefs
                ).secondary
              })`,
            }}
            to={linkInformation.link}
            tag='span'
            component={linkInformation.link && Link}
          >
            {linkInformation.label && <span>{linkInformation.label}</span>}
          </Wrapper>
        </li>
      )}
    </>
  );
};

function MegaMenuBusiness({ opened, data, maxNumberOfProducts }) {
  const secondLevelPages = data.secondLevelPages;
  const secondLevelPanels = data.secondLevelPanel;

  const [accordionOpen, setAccordionOpen] = useState(-1);
  const [mounted, setHasMounted] = useState(false);
  const [windowHeight, setWindowHeight] = useState(0);
  const { headerRef, navigationMenuRef } = useContext(HeaderContext);

  const isMobile = useWithMobileResizeListener(768);

  // const headerHeight = useContext(HeaderContext)

  // simple trick to calculate the height after 1 render, otherwise it seems to be wrong...
  useEffect(() => {
    setHasMounted(true);
  }, []);
  useEffect(() => {
    if (!mounted) return;
    setWindowHeight(window?.innerHeight);
    const scrollListener = debounce(() => {
      setWindowHeight(window?.innerHeight);
    }, 500);
    window.addEventListener('scroll', scrollListener);
    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [mounted]);

  // adding a touch listener in mobile to make a small correction when the browser address bar appears
  // this is needed because when the user "scrolls up", the actual "scroll" event is not called, so if we need to make a correction we must use another event
  useEffect(() => {
    // these checks are useful to avoid appending the event listener when not needed
    if (!opened) return;
    if (!isMobile) return;
    const touchListener = throttle(() => {
      setWindowHeight(window.innerHeight);
    }, 1000);
    document.addEventListener('touchstart', touchListener);
    return () => document.removeEventListener('touchstart', touchListener);
  }, [opened, isMobile]);

  useEffect(() => {
    if (!opened) {
      setAccordionOpen(-1);
    }
  }, [opened]);

  const menuHeightString = `${windowHeight}px - ${headerRef.current?.clientHeight}px - ${navigationMenuRef.current?.clientHeight}px`;
  return (
    <div
      data-module-name='megamenu'
      data-module-code='C-02'
      className={classNames('main-nav-secondary', {
        'main-nav-secondary--open': opened,
      })}
      style={{
        '--mega-menu-mobile': `calc(
        ${menuHeightString}
      )`,
      }}
    >
      <div
        className='main-nav-secondary__container wrapper'
        style={{
          '--item-num': `${maxNumberOfProducts}`,
        }}
      >
        {secondLevelPanels.length > 0 && (
          <MegaMenuPanel panel={secondLevelPanels[0].content}></MegaMenuPanel>
        )}
        {Object.keys(secondLevelPages).length >= 1 && (
          <div className='main-nav-secondary__submenu'>
            <ul className='main-nav-secondary__category-list'>
              {secondLevelPages?.map((secondLevelPage, key) => (
                <li
                  key={key}
                  className={classNames('main-nav-secondary__category-name', {
                    'main-nav-secondary__category-name--show-accordion':
                      accordionOpen === key,
                  })}
                  style={{
                    '--bg-category': `var(${
                      getColorsCategory(
                        secondLevelPage.content.secondLevelInternalLink?.value
                          ?.liferayFields?.taxonomyCategoryBriefs
                      ).primary
                    })`,
                  }}
                >
                  <MenuBusinessSecondLevelItem item={secondLevelPage} />
                  <button
                    className='main-nav-secondary__item main-nav-secondary__item--mobile'
                    onClick={() =>
                      setAccordionOpen((current) =>
                        current === key ? -1 : key
                      )
                    }
                  >
                    <span className='main-nav-secondary__title-category'>
                      {
                        getLinkInformation(
                          secondLevelPage.content.secondLevelInternalLink?.value
                            ?.link,
                          secondLevelPage.content.secondLevelExternalLink.value,
                          secondLevelPage.value,
                          secondLevelPage.content.secondLevelInternalLink?.value
                            ?.pageTitle?.value
                        ).label
                      }
                    </span>
                    <span className='main-nav-secondary__icon-category'>
                      <Icon
                        icon={accordionOpen === key ? 'minus' : 'plus'}
                      ></Icon>
                    </span>
                  </button>
                  <div
                    className={classNames('main-nav-tertiary', {
                      'main-nav-tertiary--show-accordion':
                        accordionOpen === key,
                    })}
                  >
                    <ul className='main-nav-tertiary__third-level-menu'>
                      {secondLevelPage.content.thirdLevelPages?.map(
                        (thirdLevelPage, key) => (
                          <MenuBusinessThirdLevelItem
                            key={key}
                            item={thirdLevelPage}
                          />
                        )
                      )}
                    </ul>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {secondLevelPanels
          .slice(1, Object.keys(secondLevelPages).length >= 1 ? 2 : 3)
          .map((panel, key) => (
            <MegaMenuPanel panel={panel.content} key={key}></MegaMenuPanel>
          ))}
      </div>
    </div>
  );
}

export default withPreview(MegaMenuBusiness);
